import { useState } from "react"
import styles from "./community-page.module.css"

interface Post {
  id: number
  title: string
  author: string
  authorAvatar: string
  content: string
  type: "帖子" | "问答" | "资源"
  status: "待审核" | "已发布" | "已拒绝"
  likes: number
  comments: number
  views: number
  createdAt: string
  group: string
}

interface Announcement {
  id: number
  title: string
  content: string
  publishedAt: string
  status: "草稿" | "已发布"
  views: number
}

const mockPosts: Post[] = [
  { id: 1, title: "React Hooks 最佳实践分享", author: "张明", authorAvatar: "张", content: "本文将分享我在项目中使用 React Hooks 的经验...", type: "帖子", status: "已发布", likes: 234, comments: 45, views: 1234, createdAt: "2024-05-10", group: "前端开发者联盟" },
  { id: 2, title: "TypeScript 高级类型技巧有哪些？", author: "李华", authorAvatar: "李", content: "想请教一下大家在实际项目中使用的 TS 高级类型技巧...", type: "问答", status: "已发布", likes: 89, comments: 23, views: 567, createdAt: "2024-05-12", group: "前端开发者联盟" },
  { id: 3, title: "2024 前端技术趋势预测", author: "王强", authorAvatar: "王", content: "今年前端领域有哪些值得关注的技术趋势？", type: "帖子", status: "待审核", likes: 156, comments: 34, views: 890, createdAt: "2024-05-14", group: "前端开发者联盟" },
  { id: 4, title: "Node.js 性能优化实战", author: "赵磊", authorAvatar: "赵", content: "分享一些在生产环境中优化 Node.js 性能的经验...", type: "资源", status: "待审核", likes: 78, comments: 12, views: 456, createdAt: "2024-05-15", group: "后端开发者圈" },
  { id: 5, title: "如何快速学习新技术？", author: "孙敏", authorAvatar: "孙", content: "作为一个新手，想请教如何高效学习新的技术栈...", type: "问答", status: "已拒绝", likes: 45, comments: 8, views: 234, createdAt: "2024-05-08", group: "产品经理交流圈" },
  { id: 6, title: "AI 辅助编程工具测评", author: "刘洋", authorAvatar: "刘", content: "对比了市面上的 AI 编程助手，各有优劣...", type: "帖子", status: "已发布", likes: 312, comments: 67, views: 1567, createdAt: "2024-05-11", group: "AI & ML 研究组" },
]

export default function CommunityPage() {
  const [activeTab, setActiveTab] = useState<"posts" | "announcements">("posts")
  const [postFilter, setPostFilter] = useState<"all" | "pending" | "published" | "rejected">("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [showAuditModal, setShowAuditModal] = useState(false)
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false)
  const [selectedPost, setSelectedPost] = useState<Post | null>(null)
  const [editingAnnouncement, setEditingAnnouncement] = useState<Partial<Announcement>>({})

  // 过滤帖子
  const filteredPosts = mockPosts.filter((post) => {
    const matchesFilter =
      postFilter === "all" ||
      (postFilter === "pending" && post.status === "待审核") ||
      (postFilter === "published" && post.status === "已发布") ||
      (postFilter === "rejected" && post.status === "已拒绝")
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.author.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesFilter && matchesSearch
  })

  // 统计
  const stats = {
    totalPosts: mockPosts.length,
    pending: mockPosts.filter((p) => p.status === "待审核").length,
    published: mockPosts.filter((p) => p.status === "已发布").length,
    totalGroups: 4,
    totalMembers: 12847,
  }

  const handleAudit = (post: Post) => {
    setSelectedPost(post)
    setShowAuditModal(true)
  }

  const handleApprove = () => {
    setShowAuditModal(false)
    setSelectedPost(null)
  }

  const handleReject = () => {
    setShowAuditModal(false)
    setSelectedPost(null)
  }

  const handleCreateAnnouncement = () => {
    setEditingAnnouncement({})
    setShowAnnouncementModal(true)
  }

  const handleSaveAnnouncement = () => {
    setShowAnnouncementModal(false)
    setEditingAnnouncement({})
  }

  // 模拟公告数据
  const mockAnnouncements: Announcement[] = [
    { id: 1, title: "平台服务升级通知", content: "平台将于5月20日进行服务升级...", publishedAt: "2024-05-15", status: "已发布", views: 2345 },
    { id: 2, title: "端午节活动公告", content: "端午节期间将有特别活动上线...", publishedAt: "2024-05-18", status: "草稿", views: 0 },
  ]

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>社区管理</h1>
        {activeTab === "announcements" && (
          <button className={styles.addBtn} onClick={handleCreateAnnouncement}>+ 发布公告</button>
        )}
      </div>

      {/* 统计卡片 */}
      <div className={styles.stats}>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.totalPosts}</span>
          <span className={styles.statLabel}>帖子总数</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.pending}</span>
          <span className={styles.statLabel}>待审核</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.published}</span>
          <span className={styles.statLabel}>已发布</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.totalGroups}</span>
          <span className={styles.statLabel}>社群数</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{(stats.totalMembers / 1000).toFixed(1)}k</span>
          <span className={styles.statLabel}>成员数</span>
        </div>
      </div>

      {/* 主标签页切换 */}
      <div className={styles.mainTabs}>
        <button className={`${styles.mainTab} ${activeTab === "posts" ? styles.activeMainTab : ""}`} onClick={() => setActiveTab("posts")}>
          帖子管理
          {stats.pending > 0 && <span className={styles.badge}>{stats.pending}</span>}
        </button>
        <button className={`${styles.mainTab} ${activeTab === "announcements" ? styles.activeMainTab : ""}`} onClick={() => setActiveTab("announcements")}>公告管理</button>
      </div>

      {activeTab === "posts" ? (
        <>
          {/* 搜索栏 */}
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder="搜索帖子标题或作者..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            <select className={styles.filterSelect} value={postFilter} onChange={(e) => setPostFilter(e.target.value as typeof postFilter)}>
              <option value="all">全部状态</option>
              <option value="pending">待审核</option>
              <option value="published">已发布</option>
              <option value="rejected">已拒绝</option>
            </select>
            <select className={styles.filterSelect}>
              <option value="">全部类型</option>
              <option value="post">帖子</option>
              <option value="qa">问答</option>
              <option value="resource">资源</option>
            </select>
          </div>

          {/* 帖子列表 */}
          <div className={styles.postList}>
            <div className={styles.postHeader}>
              <span>帖子</span>
              <span>类型</span>
              <span>作者</span>
              <span>数据</span>
              <span>状态</span>
              <span>操作</span>
            </div>
            {filteredPosts.map((post) => (
              <div key={post.id} className={styles.postRow}>
                <div className={styles.postTitle}>
                  <span className={`${styles.typeTag} ${styles[post.type]}`}>{post.type}</span>
                  <span className={styles.postName}>{post.title}</span>
                </div>
                <span className={styles.type}>{post.type}</span>
                <div className={styles.author}>
                  <span className={styles.avatar}>{post.authorAvatar}</span>
                  <span>{post.author}</span>
                </div>
                <div className={styles.stats3}>
                  <span>❤️ {post.likes}</span>
                  <span>💬 {post.comments}</span>
                  <span>👁 {post.views}</span>
                </div>
                <span className={`${styles.status} ${styles[post.status]}`}>{post.status}</span>
                <div className={styles.actions}>
                  <button className={styles.viewBtn} onClick={() => setSelectedPost(post)}>查看</button>
                  {post.status === "待审核" && (
                    <button className={styles.auditBtn} onClick={() => handleAudit(post)}>审核</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* 公告列表 */}
          <div className={styles.announcementList}>
            {mockAnnouncements.map((announcement) => (
              <div key={announcement.id} className={styles.announcementItem}>
                <div className={styles.announcementContent}>
                  <h3>{announcement.title}</h3>
                  <p>{announcement.content}</p>
                  <div className={styles.announcementMeta}>
                    <span>📅 {announcement.publishedAt}</span>
                    <span>👁 {announcement.views} 次阅读</span>
                    <span className={`${styles.status} ${styles[announcement.status]}`}>{announcement.status}</span>
                  </div>
                </div>
                <div className={styles.announcementActions}>
                  <button className={styles.editBtn}>编辑</button>
                  <button className={styles.deleteBtn}>删除</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* 审核弹窗 */}
      {showAuditModal && selectedPost && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>帖子审核</h2>
              <button className={styles.closeBtn} onClick={() => setShowAuditModal(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.previewSection}>
                <div className={styles.previewHeader}>
                  <span className={styles.previewAvatar}>{selectedPost.authorAvatar}</span>
                  <div className={styles.previewInfo}>
                    <span className={styles.previewAuthor}>{selectedPost.author}</span>
                    <span className={styles.previewDate}>{selectedPost.createdAt}</span>
                  </div>
                  <span className={`${styles.typeTag} ${styles[selectedPost.type]}`}>{selectedPost.type}</span>
                </div>
                <h3 className={styles.previewTitle}>{selectedPost.title}</h3>
                <p className={styles.previewContent}>{selectedPost.content}</p>
              </div>
              <div className={styles.auditSection}>
                <h4>审核操作</h4>
                <div className={styles.auditOptions}>
                  <button className={styles.approveBtn} onClick={handleApprove}>
                    <span>✅</span> 通过
                  </button>
                  <button className={styles.rejectBtn} onClick={handleReject}>
                    <span>❌</span> 拒绝
                  </button>
                </div>
                <div className={styles.auditReason}>
                  <label>审核理由（拒绝时必填）</label>
                  <textarea placeholder="请输入审核理由..." className={styles.textarea} rows={3}></textarea>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowAuditModal(false)}>取消</button>
              <button className={styles.submitBtn} onClick={handleApprove}>确认通过</button>
            </div>
          </div>
        </div>
      )}

      {/* 发布公告弹窗 */}
      {showAnnouncementModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>发布公告</h2>
              <button className={styles.closeBtn} onClick={() => setShowAnnouncementModal(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>公告标题</label>
                <input type="text" placeholder="输入公告标题" className={styles.input} />
              </div>
              <div className={styles.formGroup}>
                <label>公告内容</label>
                <textarea placeholder="输入公告内容..." className={styles.textarea} rows={6}></textarea>
              </div>
              <div className={styles.formGroup}>
                <label>发布范围</label>
                <div className={styles.scopeOptions}>
                  <label className={styles.radioLabel}>
                    <input type="radio" name="scope" value="all" defaultChecked />
                    全站公告
                  </label>
                  <label className={styles.radioLabel}>
                    <input type="radio" name="scope" value="group" />
                    指定社群
                  </label>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>发布时间</label>
                <input type="datetime-local" className={styles.input} />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowAnnouncementModal(false)}>取消</button>
              <button className={styles.submitBtn} onClick={handleSaveAnnouncement}>发布</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}